import { apiClient } from "./authService";

/**
 * The client file ("Patient file" in the clinic build): medical / health
 * info, signed consent forms, a staff note per visit, and attachments.
 */

export interface MedicalInfo {
  allergies: string[];
  conditions: string[];
  medications: string | null;
  bloodType: string | null;
  emergencyContactName: string | null;
  emergencyContactPhone: string | null;
  notes: string | null;
  updatedAt?: string;
}

export interface ConsentForm {
  id: string;
  title: string;
  body: string;
  requiresSignature: boolean;
  isActive: boolean;
  sortOrder: number;
  updatedAt: string;
}

export interface SignedConsent {
  id: string;
  consentFormId: string | null;
  /** The title and text as they were when signed. */
  title: string;
  body: string;
  signedName: string;
  /** PNG data URL of the drawn signature; null when none was needed. */
  signature: string | null;
  signedAt: string;
  revokedAt: string | null;
}

export interface ClientNote {
  id: string;
  appointmentId: string | null;
  body: string;
  createdBy: string | null;
  createdByName?: string | null;
  createdAt: string;
  updatedAt: string;
}


export interface ClientAttachment {
  id: string;
  appointmentId: string | null;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  /** The file type's name, from the organization's list ("Lab result"...). */
  category: string;
  title: string | null;
  uploadedByName?: string | null;
  createdAt: string;
}

export interface Visit {
  id: string;
  startAt: string;
  endAt: string;
  status: "PENDING" | "DONE" | "CANCELLED" | "IN_PROGRESS" | "REFUNDED";
  /** The note typed when booking (not the staff visit note). */
  bookingNote: string | null;
  unit: { id: string; name: string } | null;
  service: { id: string; name: string } | null;
}

export interface ClientRecord {
  client: {
    id: string;
    fullName: string;
    phone: string;
    country: string | null;
    gender: string | null;
    dateOfBirth: string | null;
    createdAt: string;
  };
  medicalInfo: MedicalInfo | null;
  consents: SignedConsent[];
  notes: ClientNote[];
  attachments: ClientAttachment[];
  upcomingVisits: Visit[];
  pastVisits: Visit[];
}

export const BLOOD_TYPES = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];

/** What the upload accepts (the backend checks the same list). */
export const ATTACHMENT_ACCEPT = ".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif,.doc,.docx";
/** Shown under the drop zone; keep in step with ATTACHMENT_ACCEPT. */
export const ATTACHMENT_ACCEPT_LABEL = "PDF, photo (JPG, PNG, WEBP, HEIC) or Word";
const ACCEPTED_EXTENSIONS = ATTACHMENT_ACCEPT.split(",");
export const isAcceptedAttachment = (file: File) =>
  ACCEPTED_EXTENSIONS.some((ext) => file.name.toLowerCase().endsWith(ext));
export const ATTACHMENT_MAX_BYTES = 15 * 1024 * 1024;

export const getClientRecord = async (clientId: string): Promise<ClientRecord> => {
  const { data } = await apiClient.get<ClientRecord>(`/clients/${clientId}/record`);
  return data;
};

export const saveMedicalInfo = async (
  clientId: string,
  info: Partial<MedicalInfo>
): Promise<MedicalInfo> => {
  const { data } = await apiClient.put<MedicalInfo>(`/clients/${clientId}/medical-info`, info);
  return data;
};

export const listConsentForms = async (includeInactive = false): Promise<ConsentForm[]> => {
  const { data } = await apiClient.get<ConsentForm[]>("/consent-forms", {
    params: includeInactive ? { all: true } : undefined,
  });
  return Array.isArray(data) ? data : [];
};

export const createConsentForm = async (
  form: Pick<ConsentForm, "title" | "body"> & Partial<ConsentForm>
): Promise<ConsentForm> => {
  const { data } = await apiClient.post<ConsentForm>("/consent-forms", form);
  return data;
};

export const updateConsentForm = async (
  id: string,
  changes: Partial<Pick<ConsentForm, "title" | "body" | "requiresSignature" | "isActive" | "sortOrder">>
): Promise<ConsentForm> => {
  const { data } = await apiClient.patch<ConsentForm>(`/consent-forms/${id}`, changes);
  return data;
};

export const signConsent = async (
  clientId: string,
  consent: { consentFormId: string; signedName: string; signature?: string }
): Promise<SignedConsent> => {
  const { data } = await apiClient.post<SignedConsent>(`/clients/${clientId}/consents`, consent);
  return data;
};

export const revokeConsent = async (clientId: string, consentId: string): Promise<SignedConsent> => {
  const { data } = await apiClient.post<SignedConsent>(
    `/clients/${clientId}/consents/${consentId}/revoke`
  );
  return data;
};

export const addClientNote = async (
  clientId: string,
  note: { body: string; appointmentId?: string | null }
): Promise<ClientNote> => {
  const { data } = await apiClient.post<ClientNote>(`/clients/${clientId}/notes`, note);
  return data;
};

export const updateClientNote = async (
  clientId: string,
  noteId: string,
  body: string
): Promise<ClientNote> => {
  const { data } = await apiClient.patch<ClientNote>(`/clients/${clientId}/notes/${noteId}`, {
    body,
  });
  return data;
};

export const deleteClientNote = async (clientId: string, noteId: string) => {
  await apiClient.delete(`/clients/${clientId}/notes/${noteId}`);
};

export const uploadClientAttachment = async (
  clientId: string,
  file: File,
  meta: { category: string; title?: string; appointmentId?: string | null }
): Promise<ClientAttachment> => {
  const form = new FormData();
  form.append("file", file);
  form.append("category", meta.category);
  if (meta.title?.trim()) form.append("title", meta.title.trim());
  if (meta.appointmentId) form.append("appointmentId", meta.appointmentId);
  const { data } = await apiClient.post<ClientAttachment>(`/clients/${clientId}/attachments`, form, {
    timeout: 120000,
  } as any);
  return data;
};

/**
 * The file's bytes (it is served only to signed-in staff), typed with its own
 * MIME type so the browser can show a PDF or an image inline.
 */
export const fetchClientAttachment = async (clientId: string, attachment: ClientAttachment): Promise<Blob> => {
  const response = await apiClient.get(`/clients/${clientId}/attachments/${attachment.id}/download`, {
    responseType: "blob",
    timeout: 120000,
  } as any);
  return new Blob([response.data as Blob], { type: attachment.mimeType });
};

export const downloadBlob = (blob: Blob, fileName: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 10000);
};

export const deleteClientAttachment = async (clientId: string, attachmentId: string) => {
  await apiClient.delete(`/clients/${clientId}/attachments/${attachmentId}`);
};

export interface RecordOptions {
  allergies: string[];
  conditions: string[];
  /** File types offered when uploading to a client file. */
  fileTypes: string[];
}

// Read once per page load; Settings refreshes it after a save.
let optionsCache: Promise<RecordOptions> | null = null;

/** The organization's allergy / condition suggestion lists. */
export const getRecordOptions = (refresh = false): Promise<RecordOptions> => {
  if (!optionsCache || refresh) {
    // Wrapped in a native Promise: this project's axios typings return their own.
    const request: Promise<RecordOptions> = Promise.resolve(
      apiClient.get<RecordOptions>("/client-record-options")
    ).then((r) => r.data);
    request.catch(() => {
      optionsCache = null;
    });
    optionsCache = request;
  }
  return optionsCache;
};

export const saveRecordOptions = async (options: Partial<RecordOptions>): Promise<RecordOptions> => {
  const { data } = await apiClient.put<RecordOptions>("/client-record-options", options);
  optionsCache = Promise.resolve(data);
  return data;
};
